import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { serverTimeAPI } from '../../services/api';

interface HeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
  isMobileMenuOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isCollapsed, onToggleSidebar, isMobileMenuOpen }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const serverOffsetRef = useRef<number>(0); // Offset in milliseconds between server and client

  // Fetch server time and calculate offset
  const syncServerTime = async () => {
    try {
      const clientRequestTime = Date.now();
      const response = await serverTimeAPI.getServerTime();
      const clientResponseTime = Date.now();
      
      if (response.data?.success && response.data?.timestamp) {
        const serverTime = response.data.timestamp;
        const networkLatency = (clientResponseTime - clientRequestTime) / 2; // Approximate latency
        const estimatedServerTime = serverTime + networkLatency;
        
        // Calculate offset: server time - client time
        serverOffsetRef.current = estimatedServerTime - clientRequestTime;
        
        // Update current time immediately
        setCurrentDateTime(new Date(estimatedServerTime));
      }
    } catch (error) {
      console.error('Failed to fetch server time, using client time:', error);
      // If server time fetch fails, use client time (offset = 0)
      serverOffsetRef.current = 0;
    }
  };

  useEffect(() => {
    // Initial server time sync
    syncServerTime();

    // Update date and time every second using server offset
    const timer = setInterval(() => {
      const clientTime = Date.now();
      const serverTime = clientTime + serverOffsetRef.current;
      setCurrentDateTime(new Date(serverTime));
    }, 1000);

    // Re-sync with server every 30 seconds to account for drift
    const syncTimer = setInterval(() => {
      syncServerTime();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(timer);
      clearInterval(syncTimer);
    };
  }, []);

  const formatDateTime = (date: Date) => {
    // Format date and time in Liberia timezone (Africa/Monrovia)
    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'Africa/Monrovia'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Africa/Monrovia'
    });
    return { dateStr, timeStr };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const handleSettings = () => {
    if (user?.role === 'REPORTS') {
      navigate('/admin/audit');
      return;
    }
    navigate('/settings');
  };

  const getToggleButtonLabel = () => {
    if (window.innerWidth < 1024) {
      return isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu';
    }
    return isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar';
  };

  const { dateStr, timeStr } = formatDateTime(currentDateTime);

  return (
    <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Sidebar Toggle Button */}
            <button
              onClick={onToggleSidebar}
              className="p-2 rounded-lg bg-gradient-to-r from-slate-100 to-slate-200 hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 shadow-sm hover:shadow-md border border-slate-200/50 focus:outline-none focus:ring-2 focus:ring-[rgb(71,161,62)] focus:ring-offset-2"
              title={getToggleButtonLabel()}
              aria-label={getToggleButtonLabel()}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen || isCollapsed ? (
                <Bars3Icon className="h-5 w-5 text-slate-600" />
              ) : (
                <XMarkIcon className="h-5 w-5 text-slate-600" />
              )}
            </button>
            
            {/* Current Date and Time - Responsive */}
            <div className="hidden md:flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
              <ClockIcon className="h-4 w-4 text-gray-500" />
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-700">{dateStr}</span>
                <span className="text-xs text-gray-500">{timeStr}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Profile dropdown */}
            <Menu as="div" className="relative">
              <div>
                <Menu.Button className="flex items-center space-x-2 sm:space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors">
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-gray-500 flex items-center justify-center ring-2 ring-white shadow-sm">
                    <span className="text-sm font-medium text-white">
                      {user?.profile?.firstName?.charAt(0) || user?.email?.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-700 truncate max-w-[120px]">
                      {user?.profile?.firstName} {user?.profile?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[120px]">{user?.role}</p>
                  </div>
                </Menu.Button>
              </div>
              
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg py-2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleProfile}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left transition-colors rounded-lg mx-1`}
                      >
                        <UserCircleIcon className="mr-3 h-5 w-5" />
                        Your Profile
                      </button>
                    )}
                  </Menu.Item>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleSettings}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex items-center px-4 py-2 text-sm text-gray-700 w-full text-left transition-colors rounded-lg mx-1`}
                      >
                        <Cog6ToothIcon className="mr-3 h-5 w-5" />
                        Settings
                      </button>
                    )}
                  </Menu.Item>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? 'bg-red-50 text-red-700' : 'text-gray-700'
                        } flex items-center px-4 py-2 text-sm w-full text-left transition-colors rounded-lg mx-1`}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;